<template>
  <div>
    <Head title="Data Pertanggung Jawaban" />
    <h1 class="mb-8 text-2xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/accountability-reports">Pertanggung Jawaban</Link>
      <span class="font-medium text-indigo-400"> /</span>
      Dokumen
    </h1>

    <!-- INFORMASI PERTANGGUNG JAWABAN -->
    <div class="w-full mb-8 bg-white border border-gray-300 rounded-md shadow">
      <div class="flex flex-col justify-between w-full gap-3 p-4 bg-gray-100 sm:flex-row sm:items-center">
        <h2 class="w-full text-lg font-semibold sm:text-xl">Informasi Pertanggung Jawaban</h2>
        <div class="flex flex-col justify-end w-full gap-3 p-4 bg-gray-100 sm:flex-row sm:items-center">
          <Button
            v-if="can('write permohonan beasiswa') && canModifyFiles && isValid" type="button" class="btn-green"
            @click.prevent="kirim"
          >
            <span><font-awesome-icon icon="fas fa-paper-plane" class="mr-2" />Kirim Pertanggung Jawaban</span>
          </Button>
          <a
            title="Laporan"
            class="rounded btn-indigo"
            :href="`/accountability-reports/${application.id}/download-surat`"
          >
            <font-awesome-icon icon="fas fa-file-contract" /> Download Surat Laporan
          </a>
        </div>
      </div>

      <!-- Desktop: tabel -->
      <div class="hidden p-4 sm:block">
        <table class="w-full text-sm border border-gray-300 rounded-md">
          <tbody>
            <tr class="border-b">
              <td class="w-1/4 p-2 font-semibold bg-gray-50">NIM Pemohon</td>
              <td class="w-1/4 p-2">: {{ application.nim }}</td>
              <td class="w-1/4 p-2 font-semibold bg-gray-50">Semester</td>
              <td class="w-1/4 p-2">: {{ application.semester }}</td>
            </tr>
            <tr class="border-b">
              <td class="p-2 font-semibold bg-gray-50">Nama Pemohon</td>
              <td class="p-2">: {{ application.nama_pemohon }}</td>
              <td class="p-2 font-semibold bg-gray-50">Nilai IPK</td>
              <td class="p-2">: {{ application.ipk }}</td>
            </tr>
            <tr class="border-b">
              <td class="p-2 font-semibold bg-gray-50">Nama Universitas</td>
              <td class="p-2">: {{ application.nama_universitas }}</td>
              <td class="p-2 font-semibold bg-gray-50">No Rekening</td>
              <td class="p-2">: {{ application.account_number }}</td>
            </tr>
            <tr>
              <td class="p-2 font-semibold bg-gray-50">Program Studi</td>
              <td class="p-2">: {{ application.nama_prodi }}</td>
              <td class="p-2 bg-gray-50" />
              <td class="p-2" />
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile: card list -->
      <div class="grid grid-cols-1 gap-2 p-4 text-sm sm:hidden">
        <div class="border border-gray-200 rounded-md">
          <div class="flex justify-between px-3 py-2 font-semibold border-b bg-gray-50"><span>NIM Pemohon</span><span>{{ application.nim }}</span></div>
          <div class="flex justify-between px-3 py-2 border-b"><span>Nama Pemohon</span><span class="text-right">{{ application.nama_pemohon }}</span></div>
          <div class="flex justify-between px-3 py-2 border-b"><span>Nama Universitas</span><span class="text-right">{{ application.nama_universitas }}</span></div>
          <div class="flex justify-between px-3 py-2 border-b"><span>Program Studi</span><span class="text-right">{{ application.nama_prodi }}</span></div>
          <div class="flex justify-between px-3 py-2 border-b"><span>Semester</span><span>{{ application.semester }}</span></div>
          <div class="flex justify-between px-3 py-2 border-b"><span>Nilai IPK</span><span>{{ application.ipk }}</span></div>
          <div class="flex justify-between px-3 py-2"><span>No Rekening</span><span class="text-right">{{ application.account_number }}</span></div>
        </div>
      </div>
    </div>

    <!-- PENGGUNAAN ANGGARAN -->
    <div class="w-full mb-8 bg-white border border-gray-300 rounded-md shadow">
      <div class="w-full p-4 bg-gray-100">
        <h2 class="text-lg font-semibold sm:text-xl">Penggunaan Anggaran</h2>
      </div>

      <!-- Desktop: tabel asli (logic tetap) -->
      <div class="hidden overflow-x-auto sm:block">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="font-bold text-left">
              <th class="px-4 py-2">Kategori</th>
              <th class="px-4 py-2">Uraian</th>
              <th class="px-4 py-2">Nominal RAB</th>
              <th class="px-4 py-2">Nominal Disetujui</th>
              <th class="px-4 py-2">Status</th>
              <th v-if="can('write permohonan beasiswa')" class="px-4 py-2">Aksi</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="detail in application.details.filter(detail => detail.file_path)" :key="detail.id">
              <tr v-if="editingId !== detail.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
                <td class="p-2 border-t">{{ detail.category }}</td>
                <td class="p-2 border-t">{{ detail.description }}</td>
                <td class="p-2 text-right border-t">{{ formatCurrency(detail.nominal_rab) }}</td>
                <td class="p-2 text-right border-t">{{ formatCurrency(detail.nominal_verifikasi) }}</td>
                <td class="p-2 text-center border-t">
                  <span v-if="detail.status == 4 || detail.status == 1 || detail.status == 0" class="px-2 py-0.5 text-xs bg-yellow-100 rounded">Draft</span>
                  <span v-else-if="detail.status == 5" class="px-2 py-0.5 text-xs bg-yellow-300 rounded">Diajukan</span>
                  <span v-else-if="detail.status == 6" class="px-2 py-0.5 text-xs bg-red-100 rounded">Revisi</span>
                  <span v-else class="px-2 py-0.5 text-xs bg-green-300 rounded">Disetujui</span>
                  <p class="mt-2 leading-4 text-gray-400">{{ detail.catatan_verifikasi }}</p>
                </td>
                <td v-if="can('write permohonan beasiswa')" class="w-px p-2 border-t">
                  <div class="flex items-center gap-2">
                    <Button
                      v-if="detail.status == 4 || detail.status == 6 || detail.status == 1 || detail.status == 0" type="button" class="btn-warning-sm"
                      title="Edit" @click.prevent="editDetailInline(detail)"
                    >
                      <font-awesome-icon icon="fas fa-pencil" />
                    </Button>
                    <Button
                      type="button" title="Lihat File" class="btn-green-sm"
                      @click.prevent="viewImage(detail.file_path)"
                    >
                      <font-awesome-icon icon="fas fa-magnifying-glass" />
                    </Button>
                  </div>
                </td>
              </tr>

              <!-- Row EDIT INLINE (desktop) -->
              <tr v-else>
                <td class="p-2 border-t" :colspan="can('write permohonan beasiswa') ? 6 : 5">
                  <div class="grid grid-cols-1 gap-3 md:grid-cols-4 md:items-end">
                    <text-input
                      :model-value="formatCurrency(editForm.nominal_rab)" type="text"
                      label="Nominal Rencana Anggaran" disabled
                    />
                    <file-input
                      v-model="editForm.file" type="file" accept="image/*,application/pdf"
                      label="Unggah File Bukti" @remove="editForm.file = null" @change="autoSaveEditInline"
                    />
                    <div v-if="editForm.uploading" class="flex items-center text-blue-600">
                      <font-awesome-icon icon="fas fa-spinner" class="mr-2 fa-spin" />Mengunggah...
                    </div>
                    <div v-if="editForm.uploadSuccess" class="flex items-center text-green-600">
                      <font-awesome-icon icon="fas fa-check-circle" class="mr-2" />Berhasil!
                    </div>
                    <div v-if="editForm.uploadError" class="flex items-center text-red-600">
                      <font-awesome-icon icon="fas fa-exclamation-circle" class="mr-2" />Gagal
                    </div>
                    <Button type="button" class="w-full btn-red md:w-auto" @click="cancelEdit">
                      Batal
                    </Button>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="application.details.length === 0">
              <td class="px-4 py-4 text-center border-t" :colspan="can('write permohonan beasiswa') ? 6 : 5">
                Belum ada data penggunaan anggaran.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile: card list (menampilkan juga mode edit inline sebagai card) -->
      <div class="p-3 space-y-3 sm:hidden">
        <template v-for="detail in application.details.filter(detail => detail.file_path)" :key="detail.id">
          <!-- VIEW MODE CARD -->
          <div v-if="editingId !== detail.id" class="border rounded">
            <div class="flex items-start justify-between gap-3 px-3 py-2 border-b rounded-t-lg bg-gray-50">
              <div>
                <p class="font-semibold leading-tight">{{ detail.category }}</p>
                <p class="text-xs text-gray-600">{{ detail.description }}</p>
              </div>
              <div class="text-right">
                <div class="text-xs text-gray-500">Nominal RAB</div>
                <div class="font-semibold">{{ formatCurrency(detail.nominal_rab) }}</div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 px-3 py-2 text-sm">
              <div class="text-gray-500">Nominal Disetujui</div>
              <div class="font-medium text-right">{{ formatCurrency(detail.nominal_verifikasi) }}</div>

              <div class="text-gray-500">Status</div>
              <div class="text-right">
                <span v-if="detail.status == 4 || detail.status == 1 || detail.status == 0" class="px-2 py-0.5 text-xs font-medium text-yellow-900 bg-yellow-200 rounded">Draft</span>
                <span v-else-if="detail.status == 5" class="px-2 py-0.5 text-xs font-medium text-yellow-900 bg-yellow-400 rounded">Diajukan</span>
                <span v-else-if="detail.status == 6" class="px-2 py-0.5 text-xs font-medium text-red-900 bg-red-200 rounded">Revisi</span>
                <span v-else class="px-2 py-0.5 text-xs font-medium text-white bg-green-500 rounded">Disetujui</span>
              </div>

              <div v-if="detail.catatan_verifikasi" class="col-span-2 mt-1 text-[13px] leading-snug text-gray-700">
                {{ detail.catatan_verifikasi }}
              </div>
            </div>

            <div v-if="can('write permohonan beasiswa')" class="flex justify-end px-3 py-2 border-t rounded-b-lg bg-gray-50">
              <Button
                v-if="detail.status == 4 || detail.status == 6 || detail.status == 0" type="button" class="mr-2 btn-warning-sm"
                @click.prevent="editDetailInline(detail)"
              >
                <font-awesome-icon icon="fas fa-pencil" />
              </Button>
              <Button type="button" class="btn-green-sm" @click.prevent="viewImage(detail.file_path)">
                <font-awesome-icon icon="fas fa-magnifying-glass" />
              </Button>
            </div>
          </div>

          <!-- EDIT MODE CARD -->
          <div v-else class="border rounded">
            <div class="px-3 py-2 font-semibold border-b rounded-t-lg bg-gray-50">
              Edit Bukti Penggunaan — {{ detail.category }}
            </div>
            <div class="grid grid-cols-1 gap-3 p-3">
              <text-input
                :model-value="formatCurrency(editForm.nominal_rab)" type="text"
                label="Nominal Rencana Anggaran" disabled
              />
              <file-input
                v-model="editForm.file" type="file" accept="image/*,application/pdf"
                label="Unggah File Bukti" @remove="editForm.file = null" @change="autoSaveEditInline"
              />
            </div>
            <div v-if="editForm.uploading" class="flex items-center px-3 py-2 text-blue-600 bg-blue-50">
              <font-awesome-icon icon="fas fa-spinner" class="mr-2 fa-spin" />Mengunggah...
            </div>
            <div v-if="editForm.uploadSuccess" class="flex items-center px-3 py-2 text-green-600 bg-green-50">
              <font-awesome-icon icon="fas fa-check-circle" class="mr-2" />Berhasil!
            </div>
            <div v-if="editForm.uploadError" class="flex items-center px-3 py-2 text-red-600 bg-red-50">
              <font-awesome-icon icon="fas fa-exclamation-circle" class="mr-2" />Gagal
            </div>
            <div class="flex justify-end px-3 py-2 border-t rounded-b-lg bg-gray-50">
              <Button type="button" class="btn-red" @click="cancelEdit">Batal</Button>
            </div>
          </div>
        </template>

        <!-- Empty state mobile -->
        <div v-if="application.details.length === 0" class="p-4 text-center text-gray-600 border rounded">
          Belum ada data penggunaan anggaran.
        </div>
      </div>

      <!-- Inline Upload - Penggunaan Anggaran (yang belum diunggah) -->
      <!-- Desktop asli -->
      <div v-if="todoUploads.length > 0" class="hidden p-6 border-t border-gray-200 sm:block">
        <h3 class="mb-4 text-lg font-semibold text-gray-700">Belum Diunggah</h3>
        <div v-for="item in todoUploads" :key="item.id" class="p-2 mb-4 border border-gray-200 rounded bg-gray-50">
          <div class="mb-2 font-medium text-indigo-600">
            {{ item.category }} - {{ item.description }}
          </div>

          <div class="grid grid-cols-1 gap-3 md:grid-cols-4 md:items-end">
            <text-input
              :model-value="formatCurrency(item.nominal_rab)" type="text" label="Nominal Rencana Anggaran"
              disabled
            />
            <file-input
              v-model="item.file" type="file" accept="image/*,application/pdf" label="Unggah File Bukti"
              @remove="item.file = null" @change="() => autoUploadAnggaran(item)"
            />
            <div v-if="item.uploading" class="flex items-center text-blue-600">
              <font-awesome-icon icon="fas fa-spinner" class="mr-2 fa-spin" />Mengunggah...
            </div>
            <div v-if="item.uploadSuccess" class="flex items-center text-green-600">
              <font-awesome-icon icon="fas fa-check-circle" class="mr-2" />Berhasil!
            </div>
            <div v-if="item.uploadError" class="flex items-center text-red-600">
              <font-awesome-icon icon="fas fa-exclamation-circle" class="mr-2" />Gagal
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile card -->
      <div v-if="todoUploads.length > 0" class="p-3 space-y-3 border-t border-gray-200 sm:hidden">
        <h3 class="text-base font-semibold text-gray-700">Belum Diunggah</h3>
        <div v-for="item in todoUploads" :key="item.id" class="border rounded">
          <div class="px-3 py-2 font-semibold border-b bg-gray-50">
            {{ item.category }} — {{ item.description }}
          </div>
          <div class="grid grid-cols-1 gap-3 p-3">
            <text-input :model-value="formatCurrency(item.nominal_rab)" type="text" label="Nominal Rencana Anggaran" disabled />
            <file-input v-model="item.file" type="file" accept="image/*,application/pdf" label="Unggah File Bukti" @remove="item.file = null" @change="() => autoUploadAnggaran(item)" />
          </div>
          <div v-if="item.uploading" class="flex items-center px-3 py-2 text-blue-600 bg-blue-50">
            <font-awesome-icon icon="fas fa-spinner" class="mr-2 fa-spin" />Mengunggah...
          </div>
          <div v-if="item.uploadSuccess" class="flex items-center px-3 py-2 text-green-600 bg-green-50">
            <font-awesome-icon icon="fas fa-check-circle" class="mr-2" />Berhasil!
          </div>
          <div v-if="item.uploadError" class="flex items-center px-3 py-2 text-red-600 bg-red-50">
            <font-awesome-icon icon="fas fa-exclamation-circle" class="mr-2" />Gagal
          </div>
        </div>
      </div>
    </div>

    <!-- FILE PERSYARATAN -->
    <div class="w-full bg-white border border-gray-300 rounded-md shadow">
      <div class="w-full p-4 bg-gray-100">
        <h2 class="text-lg font-semibold sm:text-xl">File Persyaratan</h2>
      </div>

      <!-- Desktop: tabel asli -->
      <div class="hidden overflow-x-auto sm:block">
        <table v-if="application.files.length > 0 && !editingFileId" class="w-full whitespace-nowrap">
          <thead>
            <tr class="font-bold text-left">
              <th class="px-4 py-2">Jenis File</th>
              <th class="px-4 py-2 text-center">Status Verifikasi</th>
              <th v-if="can('write permohonan beasiswa')" class="px-4 py-2">Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="file in application.files" :key="file.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
              <td class="p-2 border-t">
                {{ file.persyaratan }}
                <span v-if="file.is_required" class="px-2 py-0.5 ml-2 text-xs text-white bg-red-600 rounded">*Wajib</span>
                <span v-else class="px-2 py-0.5 ml-2 text-xs text-gray-600 bg-gray-200 rounded">Opsional</span>
              </td>
              <td class="p-2 text-center border-t">
                <span v-if="file.status == 0" class="px-2 py-0.5 text-xs bg-yellow-100 rounded">Draft</span>
                <span v-else-if="file.status == 1" class="px-2 py-0.5 text-xs rounded">-</span>
                <span v-else-if="file.status == 5" class="px-2 py-0.5 text-xs bg-yellow-300 rounded">Diajukan</span>
                <span v-else-if="file.status == 6" class="px-2 py-0.5 text-xs bg-red-100 rounded">Revisi</span>
                <span v-else class="px-2 py-0.5 text-xs bg-green-300 rounded">Disetujui</span>
                <p class="mt-1 leading-4 text-gray-400">{{ file.catatan_verifikasi }}</p>
              </td>
              <td v-if="can('write permohonan beasiswa')" class="w-px p-2 border-t">
                <div class="flex items-center gap-2">
                  <Button
                    v-if="file.status == 0 || file.status == 2 || file.status == 6" type="button"
                    class="btn-warning-sm" title="Edit" @click.prevent="editFileInline(file)"
                  >
                    <font-awesome-icon icon="fas fa-pencil" />
                  </Button>
                  <Button
                    type="button" title="Lihat File" class="btn-green-sm"
                    @click.prevent="viewImage(file.file_path)"
                  >
                    <font-awesome-icon icon="fas fa-magnifying-glass" />
                  </Button>
                  <Button
                    v-if="file.status == 0 || file.status == 2 || file.status == 6" type="button" class="btn-red-sm"
                    title="Hapus" @click.prevent="destroyFile(file.id)"
                  >
                    <font-awesome-icon icon="fas fa-trash" />
                  </Button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- EDIT INLINE (desktop) -->
        <div v-if="editingFileId" class="p-6 border-t border-gray-200">
          <div class="p-2 border border-gray-200 rounded bg-gray-50">
            <div class="mb-2 font-medium text-indigo-600">{{ editFileForm.persyaratan_nama }}</div>
            <div class="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-end">
              <file-input
                v-model="editFileForm.file" type="file" accept="image/*,application/pdf"
                label="Unggah File (gantikan)" @remove="editFileForm.file = null" @change="autoSaveEditFileInline"
              />
              <div v-if="editFileForm.uploading" class="flex items-center text-blue-600">
                <font-awesome-icon icon="fas fa-spinner" class="mr-2 fa-spin" />Mengunggah...
              </div>
              <div v-if="editFileForm.uploadSuccess" class="flex items-center text-green-600">
                <font-awesome-icon icon="fas fa-check-circle" class="mr-2" />Berhasil!
              </div>
              <div v-if="editFileForm.uploadError" class="flex items-center text-red-600">
                <font-awesome-icon icon="fas fa-exclamation-circle" class="mr-2" />Gagal
              </div>
              <Button type="button" class="w-full btn-red md:w-auto" @click="cancelEditFile">
                Batal
              </Button>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile: card list -->
      <div class="p-3 space-y-3 sm:hidden">
        <!-- List file -->
        <template v-if="application.files.length > 0 && !editingFileId">
          <div v-for="file in application.files" :key="file.id" class="border rounded">
            <div class="px-3 py-2 border-b bg-gray-50">
              {{ file.persyaratan }}
              <span v-if="file.is_required" class="ml-2 text-sm px-2 py-0.5 text-white bg-red-600 rounded">*Wajib</span>
              <span v-else class="ml-2 text-sm px-2 py-0.5 text-gray-700 bg-gray-200 rounded">Opsional</span>
            </div>
            <div class="px-3 py-2 text-sm">
              <div class="flex justify-between py-1 border-b">
                <span>Status</span>
                <span>
                  <span v-if="file.status == 0" class="px-2 py-0.5 text-xs bg-yellow-200 rounded">Draft</span>
                  <span v-else-if="file.status == 1" class="px-2 py-0.5 text-xs">-</span>
                  <span v-else-if="file.status == 5" class="px-2 py-0.5 text-xs bg-yellow-400 rounded">Diajukan</span>
                  <span v-else-if="file.status == 6" class="px-2 py-0.5 text-xs bg-red-200 rounded">Revisi</span>
                  <span v-else class="px-2 py-0.5 text-xs bg-green-400 rounded">Disetujui</span>
                </span>
              </div>
              <p class="mt-1 text-xs text-gray-600">{{ file.catatan_verifikasi }}</p>
            </div>
            <div v-if="can('write permohonan beasiswa')" class="flex justify-end px-3 py-2 border-t rounded-b-lg bg-gray-50">
              <Button
                v-if="file.status == 0 || file.status == 1 || file.status == 2 || file.status == 6" type="button" class="mr-2 btn-warning-sm"
                @click.prevent="editFileInline(file)"
              >
                <font-awesome-icon icon="fas fa-pencil" />
              </Button>
              <Button type="button" class="mr-2 btn-green-sm" @click.prevent="viewImage(file.file_path)">
                <font-awesome-icon icon="fas fa-magnifying-glass" />
              </Button>
              <Button
                v-if="file.status == 0 || file.status == 1 || file.status == 2 || file.status == 6" type="button" class="btn-red-sm"
                @click.prevent="destroyFile(file.id)"
              >
                <font-awesome-icon icon="fas fa-trash" />
              </Button>
            </div>
          </div>
        </template>

        <!-- EDIT INLINE (mobile card) -->
        <div v-if="editingFileId" class="border rounded">
          <div class="px-3 py-2 font-semibold border-b bg-gray-50">
            {{ editFileForm.persyaratan_nama }}
          </div>
          <div class="grid grid-cols-1 gap-3 p-3">
            <file-input v-model="editFileForm.file" type="file" accept="image/*,application/pdf" label="Unggah File (gantikan)" @remove="editFileForm.file = null" @change="autoSaveEditFileInline" />
          </div>
          <div v-if="editFileForm.uploading" class="flex items-center px-3 py-2 text-blue-600 bg-blue-50">
            <font-awesome-icon icon="fas fa-spinner" class="mr-2 fa-spin" />Mengunggah...
          </div>
          <div v-if="editFileForm.uploadSuccess" class="flex items-center px-3 py-2 text-green-600 bg-green-50">
            <font-awesome-icon icon="fas fa-check-circle" class="mr-2" />Berhasil!
          </div>
          <div v-if="editFileForm.uploadError" class="flex items-center px-3 py-2 text-red-600 bg-red-50">
            <font-awesome-icon icon="fas fa-exclamation-circle" class="mr-2" />Gagal
          </div>
          <div class="flex justify-end px-3 py-2 border-t rounded-b-lg bg-gray-50">
            <Button type="button" class="btn-red" @click="cancelEditFile">Batal</Button>
          </div>
        </div>
      </div>

      <!-- Inline Upload - Persyaratan yang belum diunggah -->
      <!-- Desktop asli -->
      <div
        v-if="requiredUnuploaded.length > 0 && (application.status == 0 || application.status == 1 || application.status == 2 || application.status == 6)"
        class="hidden p-6 border-t border-gray-200 sm:block"
      >
        <h3 class="mb-4 text-lg font-semibold text-gray-700">Upload File Persyaratan</h3>
        <div
          v-for="syarat in requiredUnuploaded" :key="syarat.id"
          class="p-2 mb-4 border border-gray-200 rounded bg-gray-50"
        >
          <div class="flex items-center mb-2 font-medium text-indigo-600">
            {{ syarat.nama }}
            <span v-if="syarat.is_required" class="px-2 py-0.5 ml-2 text-xs text-white bg-red-600 rounded">*Wajib</span>
            <span v-else class="px-2 py-0.5 ml-2 text-xs text-gray-600 bg-gray-200 rounded">Opsional</span>
          </div>

          <div class="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-end">
            <file-input
              v-model="syarat.file" type="file" accept=".jpg,.jpeg,.png,.pdf,image/*,application/pdf"
              label="Unggah File" @remove="syarat.file = null" @change="() => autoUploadFile(syarat)"
            />
            <div v-if="syarat.uploading" class="flex items-center text-blue-600">
              <font-awesome-icon icon="fas fa-spinner" class="mr-2 fa-spin" />Mengunggah...
            </div>
            <div v-if="syarat.uploadSuccess" class="flex items-center text-green-600">
              <font-awesome-icon icon="fas fa-check-circle" class="mr-2" />Berhasil!
            </div>
            <div v-if="syarat.uploadError" class="flex items-center text-red-600">
              <font-awesome-icon icon="fas fa-exclamation-circle" class="mr-2" />Gagal
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile card -->
      <div
        v-if="requiredUnuploaded.length > 0 && (application.status == 0 || application.status == 1 || application.status == 2 || application.status == 6)"
        class="p-3 space-y-3 border-t border-gray-200 sm:hidden"
      >
        <h3 class="text-base font-semibold text-gray-700">Upload File Persyaratan</h3>
        <div v-for="syarat in requiredUnuploaded" :key="syarat.id" class="border rounded">
          <div class="px-3 py-2 font-semibold border-b bg-gray-50">
            {{ syarat.nama }}
            <span v-if="syarat.is_required" class="ml-2 text-sm px-2 py-0.5 text-white bg-red-600 rounded">*Wajib</span>
            <span v-else class="ml-2 text-sm px-2 py-0.5 text-gray-700 bg-gray-200 rounded">Opsional</span>
          </div>
          <div class="grid grid-cols-1 gap-3 p-3">
            <file-input v-model="syarat.file" type="file" accept=".jpg,.jpeg,.png,.pdf,image/*,application/pdf" label="Unggah File" @remove="syarat.file = null" @change="() => autoUploadFile(syarat)" />
          </div>
          <div v-if="syarat.uploading" class="flex items-center px-3 py-2 text-blue-600 bg-blue-50">
            <font-awesome-icon icon="fas fa-spinner" class="mr-2 fa-spin" />Mengunggah...
          </div>
          <div v-if="syarat.uploadSuccess" class="flex items-center px-3 py-2 text-green-600 bg-green-50">
            <font-awesome-icon icon="fas fa-check-circle" class="mr-2" />Berhasil!
          </div>
          <div v-if="syarat.uploadError" class="flex items-center px-3 py-2 text-red-600 bg-red-50">
            <font-awesome-icon icon="fas fa-exclamation-circle" class="mr-2" />Gagal
          </div>
        </div>
      </div>
    </div>

    <!-- PERUBAHAN UKT -->
    <div v-if="canModifyFiles || application.ukt_nominal" class="w-full mt-8 bg-white border border-gray-300 rounded-md shadow">
      <div class="flex items-center justify-between w-full p-4 bg-gray-100">
        <h2 class="text-lg font-semibold sm:text-xl">Perubahan UKT</h2>
        <span class="text-xs text-gray-500">(Opsional)</span>
      </div>

      <!-- Info hint -->
      <div class="px-4 pt-3 pb-0 sm:px-6">
        <p class="text-sm text-gray-600">
          Jika terdapat perubahan nominal biaya ukt sebelumnya, silakan laporkan disini dengan melampirkan bukti perubahan.
        </p>
      </div>

      <!-- VIEW MODE (ada data UKT & bukan mode edit) -->
      <div v-if="application.ukt_nominal && !editingUkt" class="p-4 sm:p-6">
        <div class="p-4 border border-blue-200 rounded-md bg-blue-50">
          <div class="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <span class="font-semibold text-gray-700">Nominal UKT Baru:</span>
              <span class="ml-2 font-bold text-blue-700">{{ formatCurrency(application.ukt_nominal) }}</span>
            </div>
            <div v-if="application.ukt_catatan">
              <span class="font-semibold text-gray-700">Catatan:</span>
              <span class="ml-2 text-gray-700">{{ application.ukt_catatan }}</span>
            </div>
          </div>
          <div class="flex items-center gap-3 mt-3">
            <Button
              v-if="application.ukt_bukti_path"
              type="button" class="btn-green-sm"
              @click.prevent="viewImage(application.ukt_bukti_path)"
            >
              <font-awesome-icon icon="fas fa-magnifying-glass" class="mr-1" /> Lihat Bukti
            </Button>
            <Button
              v-if="canModifyFiles"
              type="button" class="btn-warning-sm"
              @click.prevent="startEditUkt"
            >
              <font-awesome-icon icon="fas fa-pencil" class="mr-1" /> Edit
            </Button>
          </div>
        </div>
      </div>

      <!-- FORM MODE (belum ada data ATAU mode edit) -->
      <div v-if="canModifyFiles && (editingUkt || !application.ukt_nominal)" class="p-4 sm:p-6">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-end">
          <!-- Nominal UKT -->
          <text-input
            v-model="uktFormattedNominal"
            type="text"
            label="Nominal UKT Baru (Rp)"
          />

          <!-- Upload Bukti -->
          <file-input
            v-model="uktForm.file"
            type="file"
            accept="image/*,application/pdf"
            label="Upload Bukti Perubahan UKT"
            @remove="uktForm.file = null"
          />

          <!-- Catatan (full width) -->
          <div class="md:col-span-2">
            <label class="block mb-1 text-sm font-medium text-gray-700">Catatan / Keterangan (Opsional)</label>
            <textarea
              v-model="uktForm.catatan"
              rows="2"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="Tuliskan alasan atau catatan perubahan UKT..."
            />
          </div>
        </div>

        <!-- State: uploading / success / error -->
        <div v-if="uktForm.uploading" class="flex items-center mt-3 text-blue-600">
          <font-awesome-icon icon="fas fa-spinner" class="mr-2 fa-spin" /> Menyimpan...
        </div>
        <div v-if="uktForm.uploadSuccess" class="flex items-center mt-3 text-green-600">
          <font-awesome-icon icon="fas fa-check-circle" class="mr-2" /> Berhasil disimpan!
        </div>
        <div v-if="uktForm.uploadError" class="flex items-center mt-3 text-red-600">
          <font-awesome-icon icon="fas fa-exclamation-circle" class="mr-2" /> Gagal menyimpan.
        </div>

        <!-- Action buttons -->
        <div class="flex flex-col gap-2 mt-4 sm:flex-row">
          <Button
            type="button" class="btn-green"
            :disabled="uktForm.uploading || !uktNominalRaw"
            @click.prevent="saveUkt"
          >
            <font-awesome-icon icon="fas fa-save" class="mr-2" /> Simpan Perubahan UKT
          </Button>
          <Button
            v-if="editingUkt"
            type="button" class="btn-red"
            @click.prevent="cancelEditUkt"
          >
            Batal
          </Button>
        </div>
      </div>
    </div>

    <image-viewer :src="imageSource" :show-modal="showImage" @close="showImage = false" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ImageViewer from '../../Shared/ImageViewer.vue'

export default {
  components: { Head, Link, TextInput, FileInput, FontAwesomeIcon, ImageViewer },
  layout: Layout,
  props: {
    application: Object,
    persyaratans: Array,
    todoUploads: Array,
  },
  data() {
    return {
      imageSource: '',
      showImage: false,

      // Inline edit - Anggaran
      editingId: null,
      editForm: this.$inertia.form({
        id: null,
        accountability_report_id: null,
        nominal_rab: null,   // readonly (diisi ketika masuk mode edit)
        nominal_verifikasi: null,
        file: null,
        _method: 'put',
        uploading: false,
        uploadSuccess: false,
        uploadError: false,
      }),

      // Inline edit - File Persyaratan
      editingFileId: null,
      editFileForm: this.$inertia.form({
        id: null,
        accountability_report_id: null,
        persyaratan_id: null,
        persyaratan_nama: '',
        file: null,
        _method: 'put',
        uploading: false,
        uploadSuccess: false,
        uploadError: false,
      }),

      // UKT Change form
      editingUkt: false,
      uktForm: {
        nominal: this.application.ukt_nominal || null,
        file: null,
        catatan: this.application.ukt_catatan || '',
        uploading: false,
        uploadSuccess: false,
        uploadError: false,
      },
    }
  },
  computed: {
    canModifyFiles() {
      return this.can('write permohonan beasiswa') && [0, 1, 4, 6].includes(this.application.status)
    },
    fileTypes() {
      return this.application.files.map(file => file.persyaratan_id)
    },
    requiredUnuploaded() {
      return this.persyaratans
        .filter(p => !this.fileTypes.includes(p.id))
        .map(p => ({ ...p, file: null, processing: false, uploading: false, uploadSuccess: false, uploadError: false }))
    },
    unfilledRequiredFiles() {
      return this.persyaratans.filter(p => p.is_required && !this.fileTypes.includes(p.id))
    },
    unfilledAnggaran() {
      return this.application.details.filter(detail => !detail.nominal_spj || !detail.file_path)
    },
    isValid() {
      return (
        this.unfilledRequiredFiles.length === 0 &&
        this.unfilledAnggaran.length === 0
      )
    },
    // UKT computed
    uktNominalRaw: {
      get() { return this.uktForm.nominal || null },
      set(v) { this.uktForm.nominal = v ? Number(String(v).replace(/[^0-9]/g, '')) : null },
    },
    uktFormattedNominal: {
      get() {
        if (!this.uktForm.nominal) return ''
        return new Intl.NumberFormat('id-ID', {
          style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 0,
        }).format(this.uktForm.nominal)
      },
      set(v) {
        const num = Number(String(v).replace(/[^0-9]/g, ''))
        this.uktForm.nominal = num || null
      },
    },
  },
  methods: {
    // Upload anggaran
    autoUploadAnggaran(item) {
      if (!item.file) {
        return
      }
      item.uploading = true
      item.uploadSuccess = false
      item.uploadError = false
      item.processing = true
      item.nominal_spj = item.nominal_rab

      this.$inertia.post(
        `/accountability-report-details/${item.id}`,
        {
          accountability_report_id: this.application.id,
          nominal_spj: item.nominal_spj,
          file: item.file,
          _method: 'put',
        },
        {
          onSuccess: () => {
            item.uploading = false
            item.uploadSuccess = true
            this.$swal.fire('Sukses', 'Data berhasil diunggah', 'success')
            setTimeout(() => {
              this.$inertia.reload({ preserveScroll: true })
            }, 1000)
          },
          onError: (errors) => {
            item.uploading = false
            item.uploadError = true
            const list = Object.values(errors).join('\n')
            this.$swal.fire('Gagal Upload', list, 'error')
          },
          onFinish: () => (item.processing = false),
          preserveScroll: true,
        },
      )
    },

    // ---------- Anggaran: Edit inline (row replace) ----------
    editDetailInline(detail) {
      this.editingId = detail.id
      this.editForm.id = detail.id
      this.editForm.accountability_report_id = this.application.id
      this.editForm.nominal_rab = detail.nominal_rab
      this.editForm.nominal_spj = detail.nominal_rab
      this.editForm.file = null
    },
    cancelEdit() {
      this.editingId = null
      this.editForm.reset()
      this.editForm.uploading = false
      this.editForm.uploadSuccess = false
      this.editForm.uploadError = false
    },
    autoSaveEditInline() {
      if (!this.editForm.file) {
        return
      }
      this.editForm.uploading = true
      this.editForm.uploadSuccess = false
      this.editForm.uploadError = false

      this.editForm.post(`/accountability-report-details/${this.editForm.id}`, {
        onSuccess: () => {
          this.editForm.uploading = false
          this.editForm.uploadSuccess = true
          this.$swal.fire('Sukses', 'Data berhasil diunggah', 'success')
          setTimeout(() => {
            this.cancelEdit()
            this.$inertia.reload({ preserveScroll: true })
          }, 1000)
        },
        onError: (errors) => {
          this.editForm.uploading = false
          this.editForm.uploadError = true
          const list = Object.values(errors).join('\n')
          this.$swal.fire('Gagal Simpan', list, 'error')
        },
        preserveScroll: true,
      })
    },

    // ---------- Persyaratan: Upload inline (belum diunggah) ----------
    autoUploadFile(syarat) {
      if (!syarat.file) {
        return
      }
      syarat.uploading = true
      syarat.uploadSuccess = false
      syarat.uploadError = false
      syarat.processing = true

      this.$inertia.post(
        '/accountability-report-files',
        {
          persyaratan_id: syarat.id,
          file: syarat.file,
          accountability_report_id: this.application.id,
        },
        {
          onSuccess: () => {
            syarat.uploading = false
            syarat.uploadSuccess = true
            this.$swal.fire('Sukses', 'File berhasil diunggah', 'success')
            setTimeout(() => {
              this.$inertia.reload({ preserveScroll: true })
            }, 1000)
          },
          onError: (errors) => {
            syarat.uploading = false
            syarat.uploadError = true
            const list = Object.values(errors).join('\n')
            this.$swal.fire('Gagal Upload', list, 'error')
          },
          onFinish: () => (syarat.processing = false),
          preserveScroll: true,
        },
      )
    },

    // ---------- Persyaratan: Edit inline (row replace) ----------
    editFileInline(file) {
      this.editingFileId = file.id
      this.editFileForm.id = file.id
      this.editFileForm.accountability_report_id = this.application.id
      this.editFileForm.persyaratan_id = file.persyaratan_id
      this.editFileForm.persyaratan_nama = file.persyaratan
      this.editFileForm.file = null
    },
    cancelEditFile() {
      this.editingFileId = null
      this.editFileForm.reset()
      this.editFileForm.uploading = false
      this.editFileForm.uploadSuccess = false
      this.editFileForm.uploadError = false
    },
    autoSaveEditFileInline() {
      if (!this.editFileForm.file) {
        return
      }
      this.editFileForm.uploading = true
      this.editFileForm.uploadSuccess = false
      this.editFileForm.uploadError = false

      this.editFileForm.post(`/accountability-report-files/${this.editFileForm.id}`, {
        onSuccess: () => {
          this.editFileForm.uploading = false
          this.editFileForm.uploadSuccess = true
          this.$swal.fire('Sukses', 'File berhasil diunggah', 'success')
          setTimeout(() => {
            this.cancelEditFile()
            this.$inertia.reload({ preserveScroll: true })
          }, 1000)
        },
        onError: (errors) => {
          this.editFileForm.uploading = false
          this.editFileForm.uploadError = true
          const list = Object.values(errors).join('\n')
          this.$swal.fire('Gagal Simpan', list, 'error')
        },
        preserveScroll: true,
      })
    },

    // ---------- Utils ----------
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return 'Rp 0'
      const number = typeof value === 'number' ? value : Number(value)
      if (Number.isNaN(number)) return 'Rp 0'
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(number)
    },
    viewImage(imgSource) {
      this.imageSource = imgSource
      this.showImage = true
    },
    destroyFile(id) {
      this.$swal
        .fire({
          title: 'Anda yakin hapus file persyaratan ini?',
          text: 'Anda tidak akan dapat mengembalikan data yang sudah dihapus!',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Ya',
          cancelButtonText: 'Tidak',
        })
        .then(result => {
          if (result.isConfirmed) {
            this.$inertia.delete(`/accountability-report-files/${id}`, {
              onSuccess: () => this.$inertia.reload({ preserveScroll: true }),
              onError: (errors) => {
                const list = Object.values(errors).join('\n')
                this.$swal.fire('Gagal Hapus', list, 'error')
              },
            })
          }
        })
    },
    kirim() {
      this.$swal
        .fire({
          title: 'Anda yakin kirim Pertanggung Jawaban Beasiswa ini?',
          text: 'Pastikan semua informasi yang anda kirimkan sudah sesuai!',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Ya',
          cancelButtonText: 'Tidak',
        })
        .then(result => {
          if (result.isConfirmed) {
            this.$inertia.put(`/accountability-reports/${this.application.id}/report`)
          }
        })
    },

    // ---------- UKT Change ----------
    startEditUkt() {
      this.uktForm.nominal = this.application.ukt_nominal || null
      this.uktForm.catatan = this.application.ukt_catatan || ''
      this.uktForm.file = null
      this.uktForm.uploading = false
      this.uktForm.uploadSuccess = false
      this.uktForm.uploadError = false
      this.editingUkt = true
    },
    cancelEditUkt() {
      this.editingUkt = false
      this.uktForm.file = null
      this.uktForm.uploading = false
      this.uktForm.uploadSuccess = false
      this.uktForm.uploadError = false
    },
    saveUkt() {
      if (!this.uktForm.nominal) {
        this.$swal.fire('Perhatian', 'Nominal UKT baru wajib diisi.', 'warning')
        return
      }

      this.uktForm.uploading = true
      this.uktForm.uploadSuccess = false
      this.uktForm.uploadError = false

      this.$inertia.post(
        `/accountability-reports/${this.application.id}/ukt`,
        {
          ukt_nominal: this.uktForm.nominal,
          ukt_catatan: this.uktForm.catatan,
          ukt_bukti: this.uktForm.file,
        },
        {
          forceFormData: true,
          onSuccess: () => {
            this.uktForm.uploading = false
            this.uktForm.uploadSuccess = true
            this.editingUkt = false
            this.$swal.fire('Sukses', 'Data perubahan UKT berhasil disimpan.', 'success')
            setTimeout(() => {
              this.$inertia.reload({ preserveScroll: true })
            }, 1000)
          },
          onError: (errors) => {
            this.uktForm.uploading = false
            this.uktForm.uploadError = true
            const list = Object.values(errors).join('\n')
            this.$swal.fire('Gagal Simpan', list, 'error')
          },
          preserveScroll: true,
        },
      )
    },
  },
}
</script>

<style scoped>
.form-error { color: #dc2626; }
.form-valid { color: #16a34a; }
</style>
